import * as React from 'react';
import {
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
    DialogDescription,
    Dialog,
} from '@shadcn/ui/components/ui/dialog';
import { Button } from '@shadcn/ui/components/ui/button';
import { Label } from '@shadcn/ui/components/ui/label';
import { Input } from '@shadcn/ui/components/ui/input';
import { Calendar } from '@shadcn/ui/components/ui/calendar';
import { AlarmClock, CalendarDaysIcon, Trash } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@shadcn/ui/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import dayjs from 'dayjs';
import { cn } from '@shadcn/ui/lib/utils';

import { useMutation, useQuery } from '@apollo/client';
import {
    CREATE_CONTACTS_INTERACTIONS,
    GET_CONTACT_AND_CONVERSATION_BY_ID,
    GET_CONTACT_BY_ID,
    GET_CONTACTS_WEIGHTED_SCORES,
    MARK_ACTION_AS_NOT_INTERESTED,
    SNOOZE_ACTION,
} from '@zaplify/graphql';
import { addDays, formatDistanceToNow } from 'date-fns';
import { useToast } from '@shadcn/ui/hooks/use-toast';
import { useState } from 'react';
import { getUserInteractionWeights, ContactClassificationMetadata } from '@andsend/intelligence-shared';

interface SnoozeDialogProps {
    contactFirstName: string;
    contactId: string;
    open: boolean;
    close: () => void;
    onSnooze: (days: number, contactId: string, contactFirstName: string, contactData: any) => Promise<boolean>;
}

export const useSnoozeDialogV2 = () => {
    const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
    const [contactFirstName, setContactFirstName] = useState('');
    const [contactId, setContactId] = useState('');
    const { toast } = useToast();

    const [snoozeContact] = useMutation(SNOOZE_ACTION);
    const [createContactsScoringWeights] = useMutation(CREATE_CONTACTS_INTERACTIONS);
    const { data } = useQuery(GET_CONTACT_BY_ID, {
        variables: { id: contactId },
        skip: !contactId,
    });

    const performSnooze = async (
        targetContactId: string,
        targetContactFirstName: string,
        targetContactData: any,
        snoozedUntil: Date,
        successMessage: string
    ) => {
        if (!targetContactId) {
            toast({
                title: 'Error',
                description: 'No contact selected for snoozing.',
                variant: 'destructive',
            });
            return false;
        }

        try {
            await snoozeContact({
                variables: { contactId: targetContactId, snoozedUntil },
                refetchQueries: [
                    GET_CONTACTS_WEIGHTED_SCORES,
                    {
                        query: GET_CONTACT_AND_CONVERSATION_BY_ID,
                        variables: { id: targetContactId, messageLimit: 20, noteLimit: 20 },
                    },
                ],
            });

            // Create interaction weights for scoring
            const weights = getUserInteractionWeights(
                targetContactData?.metadata as ContactClassificationMetadata,
                0.1
            );
            await createContactsScoringWeights({
                variables: {
                    userId: targetContactData?.userId || '',
                    organizationId: targetContactData?.organizationId || '',
                    contactId: targetContactId,
                    contactMetadata: targetContactData?.metadata || {},
                    weights: weights,
                    interactionType: 'SNOOZED',
                },
            });

            toast({
                title: 'Action snoozed',
                description: successMessage,
            });
            return true;
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to snooze action. Please try again.',
                variant: 'destructive',
            });
            return false;
        }
    };

    const quickSnooze = async (days: number, label: string, contact?: any) => {
        const targetContactId = contact?.id || contactId;
        const targetContactFirstName = contact?.firstName || contactFirstName;
        const targetContactData = contact || data?.Contacts_by_pk;
        const snoozedUntil = addDays(new Date(), days);

        const successMessage = `${
            targetContactFirstName.charAt(0).toUpperCase() + targetContactFirstName.slice(1)
        } will be snoozed until ${label.toLowerCase()} (${dayjs(snoozedUntil).format('dddd DD/MM')})`;

        await performSnooze(targetContactId, targetContactFirstName, targetContactData, snoozedUntil, successMessage);
    };

    const snoozeFromDialog = async (
        days: number,
        dialogContactId: string,
        dialogContactFirstName: string,
        dialogContactData: any
    ) => {
        const snoozedUntil = addDays(new Date(), days);
        const successMessage = `${
            dialogContactFirstName.charAt(0).toUpperCase() + dialogContactFirstName.slice(1)
        } will be snoozed for ${formatDistanceToNow(snoozedUntil)} until ${snoozedUntil.toLocaleDateString()}`;

        const success = await performSnooze(
            dialogContactId,
            dialogContactFirstName,
            dialogContactData,
            snoozedUntil,
            successMessage
        );
        return success;
    };

    return {
        openDialogForContact: (contactFirstName: string, contactId: string) => {
            setContactFirstName(contactFirstName);
            setContactId(contactId);
            setSnoozeDialogOpen(true);
        },
        quickSnooze,
        snoozeFromDialog,
        SnoozeDialogV2Component: () => (
            <SnoozeDialogV2
                contactFirstName={contactFirstName}
                contactId={contactId}
                open={snoozeDialogOpen}
                close={() => setSnoozeDialogOpen(false)}
                onSnooze={snoozeFromDialog}
            />
        ),
    };
};

export const SnoozeDialogV2: React.FC<SnoozeDialogProps> = ({ contactFirstName, contactId, open, close, onSnooze }) => {
    const [nbrDays, setNbrDays] = React.useState<number | null>(1);
    const [animateInput, setAnimateInput] = React.useState<boolean>(false);
    const [popoverOpen, setPopoverOpen] = React.useState<boolean>(false);
    const { toast } = useToast();
    const [markActionAsNotInterested] = useMutation(MARK_ACTION_AS_NOT_INTERESTED);
    const { data } = useQuery(GET_CONTACT_BY_ID, {
        variables: { id: contactId },
    });

    const handleSnooze = async () => {
        const success = await onSnooze(nbrDays || 1, contactId, contactFirstName, data?.Contacts_by_pk);
        if (success) {
            close();
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value ? parseInt(event.target.value, 10) : null;
        if (value === null || (!isNaN(value) && value > 0)) {
            setNbrDays(value);
        }
    };

    const handleStopSuggestingActions = () => {
        markActionAsNotInterested({
            variables: { contactId },
            refetchQueries: [
                GET_CONTACTS_WEIGHTED_SCORES,
                {
                    query: GET_CONTACT_AND_CONVERSATION_BY_ID,
                    variables: { id: contactId, messageLimit: 20, noteLimit: 20 },
                },
            ],
        }).then(() => {
            toast({
                title: 'Stopped suggesting actions',
            });
            close(); // Close the dialog after successful action
        });
    };

    const handleCalendarSelect = (date: Date) => {
        const today = dayjs();
        const selectedDate = dayjs(date);
        const duration = selectedDate.diff(today, 'day');
        setNbrDays(duration + 1);
        setAnimateInput(true);
        setTimeout(() => setAnimateInput(false), 150); // Reset animation state after 1 second
        setPopoverOpen(false); // Close the popover
    };

    const snoozeButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && nbrDays !== null) {
            snoozeButtonRef.current?.click();
        }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Snooze Action</DialogTitle>
                </DialogHeader>
                <DialogDescription>
                    In how many days would you like to reach out to{' '}
                    {contactFirstName.charAt(0).toUpperCase() + contactFirstName.slice(1)}?
                </DialogDescription>
                <div className="flex flex-col gap-4">
                    <div className="flex gap-4 justify-center items-center">
                        <Label htmlFor="snooze-input">In (days)</Label>
                        <Input
                            id="snooze-input"
                            type="number"
                            className={cn(
                                `w-[30%] transition-colors duration-150 ${
                                    animateInput ? 'bg-background-brand-primary-subtle' : 'bg-transparent'
                                }`
                            )}
                            value={nbrDays !== null ? nbrDays : ''}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                        />
                        <Popover modal open={popoverOpen} onOpenChange={setPopoverOpen}>
                            <PopoverTrigger asChild>
                                <Button size="icon" variant="outline">
                                    <CalendarDaysIcon className="w-6 h-6 stroke-1" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent>
                                <Calendar
                                    mode="single"
                                    disabled={[
                                        {
                                            before: dayjs().add(1, 'day').toDate(),
                                            after: dayjs().add(180, 'days').toDate(),
                                        },
                                    ]}
                                    selected={dayjs()
                                        .add(nbrDays || 0, 'day')
                                        .toDate()}
                                    weekStartsOn={1}
                                    onSelect={handleCalendarSelect}
                                    defaultMonth={dayjs()
                                        .add(nbrDays || 1, 'day')
                                        .toDate()}
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div className="flex justify-center">
                        <span className="text-xs text-text-tertiary">
                            Don't worry, if you receive a reply a new action will be created for you.
                        </span>
                    </div>
                </div>
                <DialogFooter className="flex sm:flex-row sm:justify-center">
                    <Tooltip delayDuration={600}>
                        <TooltipTrigger asChild>
                            <DialogClose asChild>
                                <Button variant="outline" onClick={() => handleStopSuggestingActions()}>
                                    <Trash className="w-5 h-5" />
                                    Stop suggesting actions
                                </Button>
                            </DialogClose>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-72 space-y-1">
                            <p>{`Delete action and pause follow-ups until ${
                                contactFirstName || 'this contact'
                            } replies.`}</p>
                            <p>{` If ${
                                contactFirstName || 'this contact'
                            } replies we will notify you, and follow-up suggestions will resume.`}</p>
                            <p>{` You can still find ${
                                contactFirstName || 'this contact'
                            } under My Contacts and send messages from the Conversations page.`}</p>
                        </TooltipContent>
                    </Tooltip>
                    <DialogClose asChild>
                        <Button ref={snoozeButtonRef} onClick={handleSnooze} disabled={nbrDays === null}>
                            <AlarmClock className="w-5 h-5" />
                            Snooze
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
